/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import LocaleContext from "components/i18n/LocaleContext";
import getStarLabel from "components/rating/getStarLabel";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import { StarIcon } from "lucide-react";
import { useContext, useState } from "react";

interface RatingStarsProps {
  rating: number;
  isSubmittingRating: boolean;
  onSubmitRating: (rating: number) => void;
  size?: "small" | "large";
}

function RatingStars({ rating, isSubmittingRating, onSubmitRating, size = "large" }: RatingStarsProps) {
  const selfie = useSelfie();
  const locale = useContext(LocaleContext);
  const [isRating, setIsRating] = useState(false);
  const [ratingForStar, setRatingForStar] = useState<number | null>(null);
  const viewerCanRate = selfie && selfie.isPremium;

  return (
    <div
      className={classNames("flex items-center justify-center  xl:inline-flex xl:justify-start", {
        "animate-pulse": isSubmittingRating,
        "gap-x-2": size === "large",
        "gap-x-1.5 xl:gap-x-1": size === "small",
      })}
      onMouseOver={() => {
        if (viewerCanRate) {
          setIsRating(true);
        }
      }}
      onMouseOut={() => {
        if (viewerCanRate) {
          setIsRating(false);
        }
      }}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const label = getStarLabel(star, locale);
        let isStarFilled = false;
        if (isRating) {
          isStarFilled = ratingForStar === null ? false : ratingForStar >= star;
        } else {
          isStarFilled = rating === 0 ? false : rating >= star;
        }
        return (
          <SimpleTextTooltip key={star} content={label}>
            <button
              type="button"
              disabled={!selfie || !selfie.isPremium}
              aria-label={label}
              onMouseOver={() => {
                if (viewerCanRate) {
                  setRatingForStar(star);
                }
              }}
              onMouseOut={() => {
                if (viewerCanRate) {
                  setRatingForStar(null);
                }
              }}
              onClick={() => {
                if (viewerCanRate) {
                  onSubmitRating(star);
                }
              }}
            >
              <StarIcon
                absoluteStrokeWidth={true}
                strokeWidth={size === "large" ? 1.5 : 2}
                className={classNames("text-slate-400", {
                  "fill-amber-500 text-amber-500": isStarFilled,
                  "size-7": size === "large",
                  "size-5 xl:size-4": size === "small",
                })}
              />
            </button>
          </SimpleTextTooltip>
        );
      })}
    </div>
  );
}

export default RatingStars;
